/**
 * CatalogPage — Console operatore /catalog
 *
 * Mostra il catalogo commerciale piattaforma (MOD-2-C):
 *  - 3 tab: Modules / Features / Settings
 *  - Tabella search/filter per ogni tipo
 *  - Edit inline super_admin (label, description, category, status, pricing_hint)
 *
 * @since 2026-05-17
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Tabs, Tab, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
  Alert, Snackbar, InputAdornment, useTheme,
} from '@mui/material';
import {
  ViewModule, Inventory2, Settings as SettingsIcon, Edit as EditIcon,
  Search, Refresh,
} from '@mui/icons-material';
import { auth, db } from '../firebase';
import { loadCatalog, updateCatalogItem } from '@shared/catalog/catalogService.client';
import type { CatalogSnapshot } from '@shared/catalog/catalogService';
import { SoftChip, type SoftChipTone } from '../components/SoftChip';
import type {
  ModuleDescriptor, FeatureDescriptor, SettingDescriptor,
  CatalogItemStatus, ModuleCategory, FeatureCategory,
} from '@shared/types';

type TabKey = 'modules' | 'features' | 'settings';

const STATUS_TONE: Record<CatalogItemStatus, SoftChipTone> = {
  stable: 'success',
  beta: 'warning',
  coming_soon: 'default',
  deprecated: 'error',
};

const STATUS_LABELS: Record<CatalogItemStatus, string> = {
  stable: 'Stable',
  beta: 'Beta',
  coming_soon: 'Coming soon',
  deprecated: 'Deprecated',
};

const MODULE_CATEGORY_LABELS: Record<ModuleCategory, string> = {
  core: 'Core',
  premium: 'Premium',
  marketplace: 'Marketplace',
  integration: 'Integration',
};

const FEATURE_CATEGORY_LABELS: Record<FeatureCategory, string> = {
  base: 'Base',
  premium: 'Premium',
  integration: 'Integration',
};

export const CatalogPage: React.FC = () => {
  const theme = useTheme();
  const [tab, setTab] = useState<TabKey>('modules');
  const [catalog, setCatalog] = useState<CatalogSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<CatalogItemStatus | 'all'>('all');
  const [editing, setEditing] = useState<{ type: TabKey; item: ModuleDescriptor | FeatureDescriptor | SettingDescriptor } | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({ open: false, severity: 'success', message: '' });

  const load = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const snap = await loadCatalog(db, { force });
      setCatalog(snap);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento catalogo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!catalog) return [] as Array<ModuleDescriptor | FeatureDescriptor | SettingDescriptor>;
    const source: Array<ModuleDescriptor | FeatureDescriptor | SettingDescriptor> =
      tab === 'modules' ? catalog.modules : tab === 'features' ? catalog.features : catalog.settings;
    return source.filter((item: ModuleDescriptor | FeatureDescriptor | SettingDescriptor) => {
      const hasStatus = item.type !== 'setting';
      if (filterStatus !== 'all' && hasStatus && (item as ModuleDescriptor | FeatureDescriptor).status !== filterStatus) return false;
      const hasCategory = item.type !== 'setting';
      if (filterCategory !== 'all' && hasCategory && (item as ModuleDescriptor | FeatureDescriptor).category !== filterCategory) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${item.label} ${item.description} ${item.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [catalog, tab, search, filterCategory, filterStatus]);

  const handleTabChange = (_: React.SyntheticEvent, newTab: TabKey) => {
    setTab(newTab);
    setSearch('');
    setFilterCategory('all');
    setFilterStatus('all');
  };

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !catalog) {
    return <Alert severity="error">{error || 'Errore caricamento'}</Alert>;
  }

  return (
    <Box>
      {/* Header pagina */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <ViewModule sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Catalogo piattaforma
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <SoftChip label={`${catalog.modules.length} moduli`} tone="primary" />
            <SoftChip label={`${catalog.features.length} features`} tone="info" />
            <SoftChip label={`${catalog.settings.length} settings`} tone="default" />
          </Stack>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={() => load(true)}
          sx={{ flexShrink: 0 }}
        >
          Aggiorna
        </Button>
      </Stack>

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={handleTabChange} sx={{ px: 1 }}>
          <Tab value="modules" icon={<ViewModule fontSize="small" />} iconPosition="start" label={`Moduli (${catalog.modules.length})`} />
          <Tab value="features" icon={<Inventory2 fontSize="small" />} iconPosition="start" label={`Features (${catalog.features.length})`} />
          <Tab value="settings" icon={<SettingsIcon fontSize="small" />} iconPosition="start" label={`Settings (${catalog.settings.length})`} />
        </Tabs>
      </Paper>

      {/* Filtri */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Cerca (nome, id, descrizione)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          {tab !== 'settings' && (
            <>
              <TextField
                select label="Categoria" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
                size="small" sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">Tutte</MenuItem>
                {tab === 'modules'
                  ? Object.entries(MODULE_CATEGORY_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)
                  : Object.entries(FEATURE_CATEGORY_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)
                }
              </TextField>
              <TextField
                select label="Stato" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as CatalogItemStatus | 'all')}
                size="small" sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">Tutti</MenuItem>
                {(Object.entries(STATUS_LABELS) as Array<[CatalogItemStatus, string]>).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
            </>
          )}
        </Stack>
      </Paper>

      {/* Tabella */}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>ID</TableCell>
              {tab !== 'settings' && <TableCell>Categoria</TableCell>}
              {tab === 'features' && <TableCell>Parent</TableCell>}
              {tab === 'settings' && <TableCell>Scope</TableCell>}
              {tab !== 'settings' && <TableCell>Stato</TableCell>}
              <TableCell>Descrizione</TableCell>
              <TableCell align="right">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{item.label}</TableCell>
                <TableCell><SoftChip label={item.id} tone="default" mono /></TableCell>
                {tab === 'modules' && (
                  <TableCell>
                    <SoftChip label={MODULE_CATEGORY_LABELS[(item as ModuleDescriptor).category]} tone="primary" />
                  </TableCell>
                )}
                {tab === 'features' && (
                  <>
                    <TableCell>
                      <SoftChip label={FEATURE_CATEGORY_LABELS[(item as FeatureDescriptor).category]} tone="primary" />
                    </TableCell>
                    <TableCell>
                      <SoftChip label={(item as FeatureDescriptor).parent_module} tone="default" mono />
                    </TableCell>
                  </>
                )}
                {tab === 'settings' && (
                  <TableCell>
                    <SoftChip label={(item as SettingDescriptor).scope} tone="info" />
                  </TableCell>
                )}
                {tab !== 'settings' && (
                  <TableCell>
                    <SoftChip
                      label={STATUS_LABELS[(item as ModuleDescriptor | FeatureDescriptor).status]}
                      tone={STATUS_TONE[(item as ModuleDescriptor | FeatureDescriptor).status]}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ maxWidth: 400, color: 'text.secondary', fontSize: '0.85rem' }}>{item.description}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing({ type: tab, item })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={tab === 'features' ? 7 : 6} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Nessuna voce trovata
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {editing && (
        <EditDialog
          type={editing.type}
          item={editing.item}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setSnack({ open: true, severity: 'success', message: 'Voce aggiornata' });
            setEditing(null);
            load(true);
          }}
          onError={(msg) => setSnack({ open: true, severity: 'error', message: msg })}
        />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ============================================================================
// EDIT DIALOG
// ============================================================================

interface EditDialogProps {
  type: TabKey;
  item: ModuleDescriptor | FeatureDescriptor | SettingDescriptor;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({ type, item, onClose, onSaved, onError }) => {
  const [label, setLabel] = useState(item.label);
  const [description, setDescription] = useState(item.description);
  const [category, setCategory] = useState<string>((item as ModuleDescriptor | FeatureDescriptor).category || '');
  const [status, setStatus] = useState((item as ModuleDescriptor | FeatureDescriptor).status || 'stable');
  const [pricingHint, setPricingHint] = useState((item as ModuleDescriptor | FeatureDescriptor).pricing_hint || '');
  const [saving, setSaving] = useState(false);

  const itemType: 'module' | 'feature' | 'setting' = type === 'modules' ? 'module' : type === 'features' ? 'feature' : 'setting';

  const handleSave = async () => {
    setSaving(true);
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Non autenticato');
      const patch: Record<string, unknown> = { label, description };
      if (itemType !== 'setting') {
        patch.category = category;
        patch.status = status;
        if (pricingHint) patch.pricing_hint = pricingHint;
      }
      await updateCatalogItem(db, itemType, item.id, patch, uid);
      onSaved();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="h6" component="span" fontWeight={700}>
            Modifica {itemType}
          </Typography>
          <SoftChip label={item.id} tone="default" mono />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} fullWidth size="small" />
          <TextField label="Descrizione" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={3} size="small" />
          {itemType !== 'setting' && (
            <>
              <TextField
                select label="Categoria" value={category} onChange={(e) => setCategory(e.target.value)} fullWidth size="small"
              >
                {itemType === 'module'
                  ? Object.entries(MODULE_CATEGORY_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)
                  : Object.entries(FEATURE_CATEGORY_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)
                }
              </TextField>
              <TextField
                select label="Stato" value={status} onChange={(e) => setStatus(e.target.value as CatalogItemStatus)} fullWidth size="small"
              >
                {(Object.entries(STATUS_LABELS) as Array<[CatalogItemStatus, string]>).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
              <TextField label="Pricing hint" value={pricingHint} onChange={(e) => setPricingHint(e.target.value)} fullWidth size="small" placeholder="es. Pro tier, 29 EUR/mese, Add-on..." />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} variant="outlined" size="small">Annulla</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving} size="small">
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
